import React, { useEffect } from "react";
import { Box } from "@mui/material";

export const DummyExamHeader = ({
  currentQuestion = 0,
  totalQuestions = 1,
  isRecording = false,
  timeLeft = 0,           // seconds remaining
  onTimeUp,              // callback when time reaches 0
}) => {
  // Prevent calling onTimeUp multiple times when seconds stay at 0
  useEffect(() => {
    if (timeLeft === 0 && onTimeUp && typeof onTimeUp === "function") {
      // Small protection: only call once when it first hits zero
      const timer = setTimeout(() => {
        onTimeUp();
      }, 100); // tiny delay helps avoid double calls in strict mode

      return () => clearTimeout(timer);
    }
  }, [timeLeft, onTimeUp]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const progress = totalQuestions > 0 
    ? Math.min(100, Math.max(0, ((currentQuestion + 1) / totalQuestions) * 100))
    : 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderBottom: "1px solid",
        borderColor: "divider",
        pb: 1.5,
        mb: 2,
      }}
    >
      {/* Main header row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "#0191c8",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        {/* Question counter */}
        <Box sx={{ textAlign: "center" }}>
          <Box sx={{ color: "text.secondary", fontSize: "0.85rem", mb: 0.5 }}>
            Question
          </Box>
          <Box sx={{ fontWeight: 700, fontSize: "1.35rem" }}>
            {currentQuestion + 1} <Box component="span" sx={{ color: "text.secondary" }}>/ {totalQuestions}</Box>
          </Box>
        </Box>

        {/* Timer */}
        <Box sx={{ textAlign: "center" }}>
          <Box className="dummy-timer" sx={{ color: "text.secondary", fontSize: "0.85rem", mb: 0.5 }}>
            Time Remaining
          </Box>
          <Box
            sx={{
              fontWeight: 700,
              fontSize: "1.5rem",
              color: timeLeft <= 60 ? "error.main" : "inherit",
              fontFamily: "monospace",
            }}
          >
            {formatTime(timeLeft)}
          </Box>
        </Box>

        {/* Recording status */}
        <Box sx={{ textAlign: "center" }}>
          <Box sx={{ color: "text.secondary", fontSize: "0.85rem", mb: 0.5 }}>
            Status
          </Box>
          <Box
            sx={{
              fontWeight: 600,
              color: isRecording ? "error.main" : "success.main",
              fontSize: "1.1rem",
            }}
          >
            {isRecording ? "Recording" : "Idle"}
          </Box>
        </Box>
      </Box>

      {/* Progress bar */}
      <Box
        sx={{
          height: 8,
          bgcolor: "grey.200",
          borderRadius: 4,
          overflow: "hidden",
          mt: 2,
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${progress}%`,
            bgcolor: "#0191c8",
            transition: "width 400ms ease-out",
            borderRadius: "inherit",
          }}
        />
      </Box>
    </Box>
  );
};